import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";
import { isInternalRole } from "@/lib/team";

/**
 * Admin-only login. Sends `portal: "admin"` so the backend rejects shopper /
 * merchant accounts with the same 401 message (no signal leak). Also served at
 * https://admin.cyannect.com/login when the DNS + ingress are pointed at this
 * app; in that case the backend infers portal=admin from the Host header too.
 *
 * Uses AuthContext.login() so the app-wide auth state is seeded on success —
 * otherwise ProtectedRoute would bounce the user right back to /admin/login.
 */
export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const from = useLocation().state?.from;
  const next = typeof from === "string" && from.startsWith("/admin") && !from.startsWith("/admin/login") ? from : "/admin";

  const isAdminHost = typeof window !== "undefined" &&
    window.location.hostname.startsWith("admin.");

  useEffect(() => {
    // If already signed in as admin, jump straight in — replace so Back never lands on the login page again.
    if (user && isInternalRole(user.role)) navigate(next, { replace: true });
  }, [user, navigate, next]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email.trim(), password, "admin");
      if (!res.ok) {
        toast.error(res.error || "Invalid credentials");
        return;
      }
      if (!isInternalRole(res.user.role)) {
        toast.error("This account is not authorized for the admin portal");
        return;
      }
      toast.success(`Welcome, ${res.user.name || "admin"}`);
      navigate(next, { replace: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 relative overflow-hidden"
      data-testid="admin-login-page"
    >
      {/* Subtle cyan grid backdrop — different aesthetic from the merchant/shopper login */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 20%, #06b6d4 0%, transparent 50%), radial-gradient(circle at 70% 80%, #a5b4fc 0%, transparent 45%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#06b6d4 1px, transparent 1px), linear-gradient(90deg, #06b6d4 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <form
        onSubmit={submit}
        className="relative w-full max-w-md p-8 rounded-2xl bg-slate-900/80 border border-slate-800 backdrop-blur-xl shadow-2xl"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="inline-flex items-center gap-2 text-cyan-400 text-[11px] uppercase tracking-widest font-semibold">
            <ShieldCheck className="w-4 h-4" /> Cyannect Admin
          </div>
          {isAdminHost && (
            <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">
              admin subdomain
            </span>
          )}
        </div>
        {/* Inline color to override any global h1 styles on this dark surface */}
        <h1
          className="font-display text-3xl tracking-tight"
          style={{ color: "#f8fafc" }}
          data-testid="admin-login-title"
        >
          Sign in
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Restricted to Cyannect team accounts only.
        </p>

        <label className="block text-[10px] uppercase tracking-widest text-slate-400 mt-6 mb-2">
          Email
        </label>
        <input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:border-cyan-400 text-sm font-mono"
          data-testid="admin-login-email"
        />

        <label className="block text-[10px] uppercase tracking-widest text-slate-400 mt-4 mb-2">
          Password
        </label>
        <div className="relative">
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 pl-10 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:border-cyan-400 text-sm font-mono"
            data-testid="admin-login-password"
          />
          <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
        </div>

        <button
          type="submit"
          disabled={busy}
          className="w-full mt-6 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold transition disabled:opacity-40"
          data-testid="admin-login-submit"
        >
          {busy ? "Signing in…" : "Sign in to admin"}
        </button>

        <div className="text-[11px] text-slate-500 mt-6 text-center">
          Not an admin?{" "}
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300 underline">
            Sign in to your merchant or shopper account
          </Link>
        </div>
      </form>
    </div>
  );
}
